import { Table, Typography, TableHead, TableRow, TableBody, Grid } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AcceptingRegectingAllType, AcceptingType, State } from "./interfaces";
import PendingApprovalsRow from "./PendingApprovalsRow/PendingApprovalsRow";
import { Container, StyledTableCell } from "./styles";
import { getCountersPerEmloyee } from "./utils";
import { SPACING } from "../../../_globalStore/redux/reducers/Theme/themeConstants";
import TableHeader from "../../../containers/TableHeader/TableHeader";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useSearchOptions from "../../../custom-hooks/useSearchOptions/useSearchOptions";
import useWindowSize from "../../../custom-hooks/useWindowSize/useWindowSize";
import { Page } from "../../../models/api/page";
import { SaveResponse } from "../../../models/api/save";
import { ExpenseReportsApprovalFilter } from "../../../models/expense-reports/expense-reports-approval-filter";
import {
  ClientPendingAcceptancesFilter,
  RequestType,
} from "../../../models/pending-approvals/client-pending-acceptances.filter";
import { TnAAbsenceApprovalFilter } from "../../../models/tna/tna-absences/tna-absence-approval-filter";
import { TnAExtrahoursApprovalFilter } from "../../../models/tna/tna-extra-hours/tna-extrahours-approval-filter";
import SearchTableMessage from "../../../reusable-components/SearchTableMessage/SearchTableMessage";
import CustomTablePagination from "../../../reusable-components/Table/CustomTablePagination/CustomTablePagination";
import {
  clientAcceptExpenseReports,
  clientRejectExpenseReports,
} from "../../../services/my-client-team-pending-approvals/my-client-team-expense-reports-api.service";
import { MyClientTeamPendingApprovalsApiService } from "../../../services/my-client-team-pending-approvals/my-client-team-pending-approvals-api.service";
import { TnAAbsencesApiService } from "../../../services/tna-absences/tna-absences-api.service";
import { TnAExtraHoursApiService } from "../../../services/tna-extra-hours/tna-extra-hours-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_PAPA } from "../../../utils/constants/filter-tables-constants";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";

import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";
import { ButtonsActions } from "../ButtonsActions/ButtonsActions";

const PendingClientApprovals = () => {
  const { t } = useTranslation();
  const tRP = "components.project_assignment_pending_client_approvals"; // tRP - translation root path
  const translationFields = `${tRP}.columns`;

  const { height } = useWindowSize();

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(FT_PAPA);

  const hasAccess = useAuthorization([AUTHORIZATIONS.UserIsClient]);

  const [state, setState] = useState<State>({
    loading: false,
    items: [],
    totalItems: 0,
    countersPerEmloyee: {},
    error: "",
    page: {
      pageNumber: 0,
      pageSize: 100,
    },
    isMutated: false,
  });

  const handleSetPage = (field: keyof Page, value: Page[keyof Page]): void =>
    setState((state) => ({
      ...state,
      page: { ...state.page, [field]: value },
    }));

  const loadData = useCallback(
    async (signal: AbortSignal, withoutLoader = false): Promise<void> => {
      {
        !withoutLoader &&
          setState((state) => ({
            ...state,
            loading: true,
          }));
      }

      try {
        const response = await new MyClientTeamPendingApprovalsApiService().loadItems(
          { requestTypes: [RequestType.Default] } as ClientPendingAcceptancesFilter,
          searchOptions.query,
          undefined,
          state.page,
          searchOptions.exactMatch,
          60,
          signal
        );

        if (!response.error) {
          setState((state) => ({
            ...state,
            items: response.items[0].clientPendingAcceptance,
            totalItems: response.totalItems,
            countersPerEmloyee: getCountersPerEmloyee(response.items[0].tnACounters ?? []),
            error: "",
            isMutated: false,
          }));
        } else {
          setState((state) => ({
            ...state,
            items: [],
            totalItems: 0,
            error: response.error,
            isMutated: false,
          }));
        }

        toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
          isMutated: false,
        }));
      } finally {
        setState((state) => ({
          ...state,
          ...(!withoutLoader ? { loading: false } : {}),
        }));
      }
    },
    [searchOptions, state.page]
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (state.isMutated) {
      loadData(abortController.signal);

      setState((state) => ({
        ...state,
      }));
    }

    return () => abortController.abort();
  }, [loadData, state.isMutated]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    searchOptions.query.length === 0
      ? loadData(abortController.signal)
      : (timer = setTimeout(() => loadData(abortController.signal), searchOptions.query.length === 0 ? 0 : 1000));

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [loadData, searchOptions.query, searchOptions.exactMatch]);

  const saveAbsencesCallback = useCallback(
    async (
      approveReject: "acceptedAbsences" | "rejectedAbsences",
      employeeId: string | null,
      absenceIds: string[],
      withoutLoader = false
    ) => {
      {
        !withoutLoader &&
          setState((state) => ({
            ...state,
            loading: true,
          }));
      }

      try {
        const apiService = new TnAAbsencesApiService();
        apiService.useLoadItemsPath(
          `/${approveReject === "acceptedAbsences" ? "ClientAcceptTnAAbsences" : "ClientRejectTnAAbsences"}`
        );

        const response = await apiService.loadItems(
          {
            ...(employeeId ? { employeeId } : {}),
            absenceIds,
          } as TnAAbsenceApprovalFilter,
          undefined,
          [],
          undefined,
          undefined,
          60,
          undefined
        );
        if (!response.error) {
          setState((state) => ({
            ...state,
            ...(!withoutLoader ? { isMutated: true } : {}),
          }));
        }

        toastifyResponse(response);
      } catch (error: any) {
        setState((state) => ({
          ...state,
          error: error?.message ?? error,
        }));
      } finally {
        setState((state) => ({
          ...state,
          ...(!withoutLoader ? { loading: false } : {}),
        }));
      }
    },
    []
  );

  const saveEHCallback = useCallback(
    async (
      approveReject: "acceptedExtraHours" | "rejectedExtraHours",
      employeeId: string | null,
      extrahoursIds: string[],
      withoutLoader = false
    ) => {
      {
        !withoutLoader &&
          setState((state) => ({
            ...state,
            loading: true,
          }));
      }

      try {
        const apiService = new TnAExtraHoursApiService();
        apiService.useLoadItemsPath(
          `/${approveReject === "acceptedExtraHours" ? "ClientAcceptTnAExtraHours" : "ClientRejectTnAExtraHours"}`
        );

        const response = await apiService.loadItems(
          {
            ...(employeeId ? { employeeId } : {}),
            extraHourIds: extrahoursIds,
          } as TnAExtrahoursApprovalFilter,
          undefined,
          [],
          undefined,
          undefined,
          60,
          undefined
        );
        if (!response.error) {
          setState((state) => ({
            ...state,
            ...(!withoutLoader ? { isMutated: true } : {}),
          }));
        }

        toastifyResponse(response);
      } catch (error: any) {
        setState((state) => ({
          ...state,
          error: error?.message ?? error,
        }));
      } finally {
        setState((state) => ({
          ...state,
          ...(!withoutLoader ? { loading: false } : {}),
        }));
      }
    },
    []
  );

  const saveExpenseReportsCallback = useCallback(async (ids: string[], type: AcceptingType, withoutLoader = false) => {
    {
      !withoutLoader &&
        setState((state) => ({
          ...state,
          loading: true,
        }));
    }

    try {
      let response: SaveResponse | null = null;

      if (type === "acceptedExpenseReports") {
        response = await clientAcceptExpenseReports({
          expenseReportIDs: ids,
        } as ExpenseReportsApprovalFilter);
      } else if (type === "rejectedExpenseReports") {
        response = await clientRejectExpenseReports({
          expenseReportIDs: ids,
        } as ExpenseReportsApprovalFilter);
      }
      // const response = await saveClientAcceptances({
      //   id: uuidv4(),
      //   isDeleted: false,
      //   acceptedAbsences: type === "acceptedAbsences" ? ids : [],
      //   rejectedAbsences: type === "rejectedAbsences" ? ids : [],
      //   acceptedExtraHours: type === "acceptedExtraHours" ? ids : [],
      //   rejectedExtraHours: type === "rejectedExtraHours" ? ids : [],
      //   acceptedExpenseReports: type === "acceptedExpenseReports" ? ids : [],
      //   rejectedExpenseReports: type === "rejectedExpenseReports" ? ids : [],
      // } as ClientAcceptancesSTO);
      if (response) {
        if (!response.error) {
          setState((state) => ({
            ...state,
            ...(!withoutLoader ? { isMutated: true } : {}),
          }));
        }

        toastifyResponse(response);
      }
    } catch (error: any) {
      setState((state) => ({
        ...state,
        error: error?.message ?? error,
      }));
    } finally {
      setState((state) => ({
        ...state,
        ...(!withoutLoader ? { loading: false } : {}),
      }));
    }
  }, []);

  const actionsHandler = useCallback(
    async (id: string, type: AcceptingType, employeeId: string) => {
      if (type === "acceptedAbsences" || type === "rejectedAbsences") {
        await saveAbsencesCallback(type, employeeId, [id]);
      } else if (type === "acceptedExtraHours" || type === "rejectedExtraHours") {
        await saveEHCallback(type, employeeId, [id]);
      } else {
        await saveExpenseReportsCallback([id], type);
      }
    },
    [saveAbsencesCallback, saveEHCallback, saveExpenseReportsCallback]
  );

  const buttonActions = useCallback(
    async (type: AcceptingRegectingAllType) => {
      setState((state) => ({
        ...state,
        loading: true,
      }));

      const absenses = state.items.map((item) => item.tnAAbsence?.id).filter((item) => !!item) as string[];

      const extraHours = state.items.map((item) => item.tnAExtraHours?.id).filter((item) => !!item) as string[];

      const expensesReports = state.items.map((item) => item.expenseReport?.id).filter((item) => !!item) as string[];

      try {
        if (type === "acceptedAll") {
          !!absenses.length && (await saveAbsencesCallback("acceptedAbsences", null, absenses, true));
          !!extraHours.length && (await saveEHCallback("acceptedExtraHours", null, extraHours, true));
          !!expensesReports.length &&
            (await saveExpenseReportsCallback(expensesReports, "acceptedExpenseReports", true));
        } else {
          !!absenses.length && (await saveAbsencesCallback("rejectedAbsences", null, absenses, true));
          !!extraHours.length && (await saveEHCallback("rejectedExtraHours", null, extraHours, true));
          !!expensesReports.length &&
            (await saveExpenseReportsCallback(expensesReports, "rejectedExpenseReports", true));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
          isMutated: true,
        }));
      }
    },
    [saveAbsencesCallback, saveEHCallback, saveExpenseReportsCallback, state.items]
  );

  const getWidth = (fieldTitle: string): number => {
    const field = columns.find((obj) => obj.field === fieldTitle);

    if (field) return field.width;

    return 0;
  };

  const columns = [
    {
      title: t("buttons.actions"),
      field: "actions",
      width: 20,
    },
    {
      title: `${t("common_words.employee")} | ${t("common_words.legal_entity")}`,
      field: "employee",
      width: 15,
    },
    {
      title: t(`${translationFields}.date`),
      field: "date",
      width: 15,
    },
    {
      title: t(`${translationFields}.data`),
      field: "absense_extrahours",
      width: 15,
    },
    {
      title: t(`${translationFields}.info`),
      field: "info",
      width: 15,
    },
    {
      title: t(`${translationFields}.absence_counters`),
      field: "counters",
      width: 15,
    },
  ];

  const currHeight =
    height -
    (SPACING[9] * 2 +
      (document.getElementById("header")?.clientHeight ?? 0) +
      SPACING[7] +
      (document.getElementById("tableHeader")?.clientHeight ?? 0) +
      SPACING[7] +
      SPACING[4] +
      45);

  return (
    <>
      {hasAccess && (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <Grid sx={{ marginBottom: "10px" }}>
            <TableHeader
              searchOptions={searchOptions}
              tableTitle={`${t(`${tRP}.table_title`)}`}
              handleChangeSearchOptions={handleChangeSearchOptions}
              {...(state.items.length > 0 &&
                !state.loading && {
                  quickFilters: (
                    <ButtonsActions
                      disabled={state.loading}
                      buttonActions={buttonActions}
                    />
                  ),
                })}
            />
          </Grid>
          <>
            <Container
              sx={{
                height: `${currHeight}px`,
                overflow: "auto",
                "& .MuiTableHead-root .MuiTableRow-root": {
                  position: "relative",
                },

                "& > tr, td, th": {
                  lineHeight: "1rem",
                },
              }}
            >
              {state.totalItems > 0 ? (
                <Grid
                  sx={{
                    height: `${currHeight}px`,
                    overflow: "auto",
                    "& .MuiTableHead-root .MuiTableRow-root": {
                      position: "relative",
                    },

                    "& > tr, td, th": {
                      lineHeight: "1rem",
                    },
                  }}
                >
                  <Table
                    size={"small"}
                    stickyHeader={true}
                  >
                    <TableHead>
                      <TableRow>
                        {columns.map((column, idx) => (
                          <StyledTableCell
                            key={idx}
                            align="center"
                            width={column.width}
                          >
                            <Typography>{column.title}</Typography>
                          </StyledTableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {state.items.map((item, idx) => (
                        <React.Fragment key={idx}>
                          <PendingApprovalsRow
                            item={item}
                            getWidth={getWidth}
                            actionsHandler={actionsHandler}
                            disableButtons={state.loading}
                            countersPerEmloyee={state.countersPerEmloyee}
                          />
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </Grid>
              ) : (
                <SearchTableMessage
                  loading={state.loading}
                  searchOptions={searchOptions}
                  error={state.error}
                />
              )}
            </Container>

            <CustomTablePagination
              totalItems={state.totalItems}
              pageNumber={state.page.pageNumber}
              handleChangePageNumber={(p) => handleSetPage("pageNumber", p)}
              pageSize={state.page.pageSize}
              handleChangePageSize={(s) => {
                handleSetPage("pageSize", s);
                handleSetPage("pageNumber", 0);
              }}
              labelRowsPerPage={"Entries per page"}
            />
          </>
        </LoadingOverlay>
      )}
    </>
  );
};

export default PendingClientApprovals;
